import React, { FormEventHandler } from 'react';
import { Form, TextInputControl, DropdownControl } from '@ver-uds/uswds-react';
import { HiringSite, State } from 'eve-account-client';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { useFormContext } from 'react-hook-form';
import { useStatesQuery } from '../../services';

export type AddEditHiringSiteFormProps = {
  id: string;
  onSubmit: FormEventHandler;
};

const AddEditHiringSiteForm = ({ id, onSubmit }: AddEditHiringSiteFormProps): JSX.Element => {
  const { isLoading: statesIsLoading, data: statesData } = useStatesQuery();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, errors } = useFormContext<HiringSite>();

  return (
    <Form id={id} variant="large" onSubmit={onSubmit} noValidate>
      {statesIsLoading ? (
        <>
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </>
      ) : (
        <>
          <input type="hidden" name="id" ref={register()} />
          <TextInputControl
            id={`${id}-input-street-address`}
            name="line1"
            label="Street Address"
            required
            ref={register({
              required: {
                value: true,
                message: 'Street Address is required.',
              },
              maxLength: {
                value: 50,
                message: 'Please enter no more than 50 characters.',
              },
              pattern: {
                value: /^[a-zA-Z0-9\-‒–—―. ]*$/,
                message: 'This field cannot contain special characters, aside from dashes, periods, and spaces.',
              },
            })}
            errorId={errors.line1 && 'street-address-error'}
            errorMessage={errors.line1?.message}
          />
          <TextInputControl
            id={`${id}-input-suite`}
            name="line2"
            label="Suite/Other"
            ref={register({
              maxLength: {
                value: 50,
                message: 'Please enter no more than 50 characters.',
              },
              pattern: {
                value: /^[a-zA-Z0-9\-‒–—―. ]*$/,
                message: 'This field cannot contain special characters, aside from dashes, periods, and spaces.',
              },
            })}
            errorId={errors.line2 && 'suite-other-error'}
            errorMessage={errors.line2?.message}
          />
          <TextInputControl
            id={`${id}-input-city`}
            name="city"
            label="City"
            className="grid-col-7"
            required
            ref={register({
              required: {
                value: true,
                message: 'City is required.',
              },
              maxLength: {
                value: 30,
                message: 'Please enter no more than 30 characters.',
              },
              pattern: {
                value: /^[a-zA-Z0-9\-‒–—― ]*$/,
                message: 'This field cannot contain special characters, aside from dashes and spaces.',
              },
            })}
            errorId={errors.city && 'city-error'}
            errorMessage={errors.city?.message}
          />
          <DropdownControl
            id={`${id}-dropdown-state`}
            name="stateCode"
            label="State"
            className="grid-col-7"
            required
            ref={register({
              required: {
                value: true,
                message: 'State is required.',
              },
            })}
            errorId={errors.stateCode && 'state-error'}
            errorMessage={errors.stateCode?.message}
          >
            <option value="">- Select -</option>
            {statesData?.map((state: State) => (
              <option key={state.code} value={state.code}>
                {state.displayName}
              </option>
            ))}
          </DropdownControl>
          <TextInputControl
            id={`${id}-zip-code`}
            name="zipCode"
            label="Zip Code"
            className="grid-col-7"
            required
            hint="5 digits, numbers only"
            ref={register({
              validate: (value: string) => {
                if (!/^\d{5}$/.exec(value)) {
                  return 'Please enter a 5-digit Zip Code.';
                }
                return true;
              },
            })}
            errorId={errors.zipCode && 'zip-code-error'}
            errorMessage={errors.zipCode?.message}
          />
        </>
      )}
    </Form>
  );
};

export default AddEditHiringSiteForm;
